/**
 * 入力欄に載せたチャット添付と、送信時の振り分け。
 * 添付した時点では振り分けず、送信時にまとめて決める。
 */
import { imageBlockSrc, type DocumentContentBlock, type ImageContentBlock } from "@/lib/chat";
import {
	classifyAttachment,
	MAX_ATTACHMENT_BYTES_PER_MESSAGE,
	MAX_DOCUMENTS_PER_MESSAGE,
	processDocumentFile,
	toDocumentBlock,
} from "@/lib/document-attachments";
import {
	fetchImageBlob,
	ImageAttachmentError,
	MAX_IMAGES_PER_MESSAGE,
	processImageFile,
	type ComposerImage,
} from "@/lib/image-attachments";
import type { UploadedFile } from "@/lib/uploaded-file-block";

export type ComposerAttachment =
	| { id: string; kind: "file"; file: File }
	/** 送信失敗・会話分岐で入力欄へ戻した、ワークスペースへ置き済みのファイル。 */
	| { id: string; kind: "uploaded"; uploaded: UploadedFile };

export function attachmentFromFile(file: File): ComposerAttachment {
	return { id: crypto.randomUUID(), kind: "file", file };
}

export function attachmentFromUploaded(uploaded: UploadedFile): ComposerAttachment {
	return { id: crypto.randomUUID(), kind: "uploaded", uploaded };
}

export interface RoutedAttachments {
	images: ImageContentBlock[];
	documents: DocumentContentBlock[];
	/** 本文に入りきらないファイル。 */
	overflow: File[];
}

async function processImageOrNull(file: File): Promise<ComposerImage | null> {
	try {
		return await processImageFile(file);
	} catch (err) {
		if (err instanceof ImageAttachmentError) return null;
		throw err;
	}
}

function toImageBlock(image: ComposerImage): ImageContentBlock {
	return {
		type: "image",
		source: { type: "base64", media_type: image.mediaType, data: image.data },
	};
}

/**
 * 添付を本文 (image / document ブロック) に入れる分と入りきらない分へ振り分ける。件数・合計サイズの
 * 上限を超える分、変換できない画像、UTF-8 として読めないテキスト、それ以外の形式は入りきらない分にする。
 */
export async function routeAttachments(files: readonly File[]): Promise<RoutedAttachments> {
	const routed: RoutedAttachments = { images: [], documents: [], overflow: [] };
	let totalBytes = 0;
	for (const file of files) {
		const route = classifyAttachment(file);
		if (route === "image" && routed.images.length < MAX_IMAGES_PER_MESSAGE) {
			const image = await processImageOrNull(file);
			if (image !== null && totalBytes + image.byteSize <= MAX_ATTACHMENT_BYTES_PER_MESSAGE) {
				routed.images.push(toImageBlock(image));
				totalBytes += image.byteSize;
				continue;
			}
		} else if (
			route === "document" &&
			routed.documents.length < MAX_DOCUMENTS_PER_MESSAGE &&
			// 読み込む前に弾けるよう、元ファイルのサイズで判定する。
			totalBytes + file.size <= MAX_ATTACHMENT_BYTES_PER_MESSAGE
		) {
			const document = await processDocumentFile(file);
			if (document !== null) {
				routed.documents.push(toDocumentBlock(document));
				totalBytes += document.byteSize;
				continue;
			}
		}
		routed.overflow.push(file);
	}
	return routed;
}

function base64ToBytes(data: string): Uint8Array<ArrayBuffer> {
	return Uint8Array.from(atob(data), (char) => char.charCodeAt(0));
}

async function imageFile(block: ImageContentBlock, index: number): Promise<File> {
	const type = block.source.media_type;
	const body =
		block.source.type === "url"
			? await fetchImageBlob(imageBlockSrc(block))
			: base64ToBytes(block.source.data);
	return new File([body], `画像 ${index + 1}.${type.split("/")[1]}`, { type });
}

function documentFile(block: DocumentContentBlock, index: number): File | null {
	const { source } = block;
	if (source.type === "omitted") return null;
	return source.type === "base64"
		? new File([base64ToBytes(source.data)], block.title ?? `文書 ${index + 1}.pdf`, {
				type: source.media_type,
			})
		: new File([source.data], block.title ?? `文書 ${index + 1}.txt`, { type: source.media_type });
}

/**
 * 送信済みメッセージの添付を入力欄の添付へ戻す (会話分岐で元メッセージを下書きに載せる用)。
 * 履歴 API が本体を外した画像は画像エンドポイントから取り直す。本体を外した文書は取り直す手段がないため引き継がない。
 */
export async function composerAttachmentsFromMessage(message: {
	images: readonly ImageContentBlock[];
	documents: readonly DocumentContentBlock[];
	uploadedFiles: readonly UploadedFile[];
}): Promise<ComposerAttachment[]> {
	const images = await Promise.all(message.images.map(imageFile));
	const documents = message.documents
		.map(documentFile)
		.filter((file): file is File => file !== null);
	return [
		...[...images, ...documents].map(attachmentFromFile),
		...message.uploadedFiles.map(attachmentFromUploaded),
	];
}
